// What makes one employment or unemployment entry valid, and when the driver is
// told about it.
//
// Two rules shape everything here. Validation is per entry, at that entry's own
// Save — never a pass over the whole list that marks entries the driver has not
// opened. And an unanswered certification is a missing answer, not a false one.
// Contact permission is the exception: a new entry starts as yes, or no when
// the job is current, and the dates keep that answer in step.
//
// Phone and email verdicts come from src/lib/contact.utils, which mirrors the
// API's normalizers. That matters here: a contact captured on this page becomes
// the VoE contact, so if this file accepted a value the backend discards, the
// driver would supply a contact the system treats as absent.

import { cleanPhoneNumber, sanitizeEmail } from 'src/lib/contact.utils'

export type EntryKind = 'employment' | 'unemployment'

export interface EntryDraft {
  kind: EntryKind
  /** Set when the driver picked a carrier from the registry. */
  carrierSelected?: boolean
  /**
   * The driver said this was not a driving or DOT-regulated job, so no carrier
   * was expected. Sheet-only: it decides telemetry, and is never submitted.
   */
  nonDrivingJob?: boolean
  companyName?: string | null
  /** Known for a registry carrier; a driver may also type one themselves. */
  dotNumber?: string | null
  city?: string | null
  state?: string | null
  /** Filled in by the address lookup when it has them. */
  street?: string | null
  zipCode?: string | null
  /** Inclusive month indices; null means not yet set. */
  startMonth?: number | null
  endMonth?: number | null
  /** True when the entry runs to the present, in which case endMonth is unused. */
  isCurrent?: boolean
  position?: string | null
  reasonForLeaving?: string | null
  unemploymentReason?: string | null
  isDriverTerminated?: boolean | null
  isDriverSubjectToFMCSRs?: boolean | null
  isJobDesignatedAsSafetySensitiveFunctionUnderDOT?: boolean | null
  shouldContactEmployer?: boolean | null
  /** What the driver drove here. Optional — recruiters filter on it, not DOT. */
  equipmentTypes?: string[]
  /** Who to ask at this employer. Optional, like the rest of the contact. */
  contactName?: string | null
  /** Optional, but a malformed one is still an error — correct it or clear it. */
  phone?: string | null
  email?: string | null
}

export type FieldName =
  | 'companyName'
  | 'dotNumber'
  | 'city'
  | 'state'
  | 'startMonth'
  | 'endMonth'
  | 'position'
  | 'reasonForLeaving'
  | 'unemploymentReason'
  | 'isDriverTerminated'
  | 'isDriverSubjectToFMCSRs'
  | 'isJobDesignatedAsSafetySensitiveFunctionUnderDOT'
  | 'shouldContactEmployer'
  | 'phone'
  | 'email'

/** Errors state the rule, not the failure: what to do, not that something is wrong. */
export const MESSAGES = {
  companyName: 'Company name is required.',
  dotNumber: 'DOT number can only contain digits.',
  city: 'City is required for manual entries.',
  state: 'State is required.',
  startRequired: 'Start date is required.',
  startFuture: 'Start date is in the future.',
  endRequired: 'End date is required.',
  endFuture: 'End date is in the future.',
  endBeforeStart: 'End date is before the start date.',
  position: 'Job title is required.',
  reasonForLeaving: 'Reason for leaving is required.',
  unemploymentReason: 'A brief reason is required.',
  certification: 'Answer this question.',
  phone: 'Enter a 10-digit phone number.',
  email: 'Please enter a valid email',
} as const

export type Errors = Partial<Record<FieldName, string>>

/**
 * Fields checked the moment the driver leaves them. A half-typed phone number is
 * unfinished, not wrong, so nothing fires while it still has focus.
 */
export const FORMAT_FIELDS: FieldName[] = ['phone', 'email', 'dotNumber']

const hasText = (value?: string | null): boolean =>
  Boolean(value && value.trim())

/**
 * The shared normalizer decides, but only for numbers it will not rewrite.
 *
 * `cleanPhoneNumber` coerces anything that is not already a US number into one:
 * it keeps the last ten digits of a longer value, and drops the first digit of
 * any eleven-digit value that does not start with 1. Either way a foreign
 * number comes back as a real US number belonging to someone else. This field
 * gets dialled for verification, so refuse those shapes rather than quietly
 * rewriting them.
 */
const phoneIsValid = (value: string): boolean => {
  const digits = value.replace(/\D/g, '')
  const isNorthAmerican =
    digits.length === 10 || (digits.length === 11 && digits.startsWith('1'))
  if (!isNorthAmerican) return false
  return cleanPhoneNumber(value) !== null
}

const emailIsValid = (value: string): boolean => {
  const normalized = sanitizeEmail(value)
  if (normalized === null) return false
  const [local, domain] = normalized.split('@')
  return (
    !local.startsWith('.') &&
    !local.endsWith('.') &&
    !normalized.includes('..') &&
    domain
      .split('.')
      .every(
        (label) =>
          label.length > 0 && !label.startsWith('-') && !label.endsWith('-')
      )
  )
}

export const isAcceptableEmail = (value?: string | null): boolean =>
  !hasText(value) || emailIsValid(value as string)

export const isAcceptableDotNumber = (value?: string | null): boolean =>
  !hasText(value) || /^\d+$/.test((value as string).trim())

/**
 * Format rules only — safe to run on blur, because nothing here can complain
 * about a field the driver has not filled in yet.
 */
export const validateFormat = (draft: EntryDraft): Errors => {
  const errors: Errors = {}
  if (hasText(draft.phone) && !phoneIsValid(draft.phone as string)) {
    errors.phone = MESSAGES.phone
  }
  if (!isAcceptableEmail(draft.email)) {
    errors.email = MESSAGES.email
  }
  if (!isAcceptableDotNumber(draft.dotNumber)) {
    errors.dotNumber = MESSAGES.dotNumber
  }
  return errors
}

/**
 * Everything an entry needs before it can be saved, format rules included.
 * `nowMonth` is passed in rather than read from the clock so the caller — and
 * the tests — control what "the future" means.
 */
export const validateEntry = (draft: EntryDraft, nowMonth: number): Errors => {
  const errors: Errors = { ...validateFormat(draft) }
  const isEmployment = draft.kind === 'employment'

  if (isEmployment) {
    if (!hasText(draft.companyName)) errors.companyName = MESSAGES.companyName
    // A carrier chosen from the registry brings its own address; a typed
    // employer needs a location or the verification has nowhere to go.
    if (!draft.carrierSelected) {
      if (!hasText(draft.city)) errors.city = MESSAGES.city
      if (!hasText(draft.state)) errors.state = MESSAGES.state
    }
    if (!hasText(draft.position)) errors.position = MESSAGES.position
  }

  if (draft.startMonth == null) {
    errors.startMonth = MESSAGES.startRequired
  } else if (draft.startMonth > nowMonth) {
    errors.startMonth = MESSAGES.startFuture
  }

  if (!draft.isCurrent) {
    if (draft.endMonth == null) {
      errors.endMonth = MESSAGES.endRequired
    } else if (draft.startMonth != null && draft.endMonth < draft.startMonth) {
      errors.endMonth = MESSAGES.endBeforeStart
    } else if (draft.endMonth > nowMonth) {
      // A job that has ended cannot end after today. The application's zod
      // schema has always refused this; so must the per-entry check that will
      // replace it.
      errors.endMonth = MESSAGES.endFuture
    }
  }

  if (isEmployment) {
    if (!draft.isCurrent && !hasText(draft.reasonForLeaving)) {
      errors.reasonForLeaving = MESSAGES.reasonForLeaving
    }
    // Asked of every entry. For a current job it arrives already answered,
    // entailed by still working there.
    if (draft.isDriverTerminated == null) {
      errors.isDriverTerminated = MESSAGES.certification
    }
    if (draft.isDriverSubjectToFMCSRs == null) {
      errors.isDriverSubjectToFMCSRs = MESSAGES.certification
    }
    // Related to the FMCSR answer but never derived from it
    if (draft.isJobDesignatedAsSafetySensitiveFunctionUnderDOT == null) {
      errors.isJobDesignatedAsSafetySensitiveFunctionUnderDOT =
        MESSAGES.certification
    }
    // Asked of every employer. A new entry arrives with a default — yes,
    // unless the job is current — and the dates keep that default current.
    // Null is still missing: an entry saved before the default existed, or
    // one a recruiter left blank, still has to be answered.
    if (draft.shouldContactEmployer == null) {
      errors.shouldContactEmployer = MESSAGES.certification
    }
  } else if (!hasText(draft.unemploymentReason)) {
    errors.unemploymentReason = MESSAGES.unemploymentReason
  }

  return errors
}

export const isValidEntry = (draft: EntryDraft, nowMonth: number): boolean =>
  Object.keys(validateEntry(draft, nowMonth)).length === 0

/**
 * The certifications an employment entry is asked for.
 *
 * One list, used both to decide what to render and to decide what is missing.
 * These were separate and drifted: after termination and contact permission
 * became universal, the "missing" side still treated each as conditional, so an
 * entry lacking one was reported as a broken rule rather than an unanswered
 * question — red, with nothing useful to say.
 */
export const CERTIFICATION_FIELDS: FieldName[] = [
  'isDriverTerminated',
  'isDriverSubjectToFMCSRs',
  'isJobDesignatedAsSafetySensitiveFunctionUnderDOT',
  'shouldContactEmployer',
]

export const certificationsFor = (draft: EntryDraft): FieldName[] =>
  draft.kind === 'employment' ? CERTIFICATION_FIELDS : []

/** The certifications only a driver can give, for the entries someone else created. */
export const missingCertifications = (draft: EntryDraft): FieldName[] =>
  certificationsFor(draft).filter(
    (field) => draft[field as keyof EntryDraft] == null
  )

export const attentionLabel = (count: number): string =>
  count === 1 ? '1 field needs attention' : `${count} fields need attention`
